import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Check, Copy, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * "Boost your chances" block shared by the lottery and the quiz:
 * gold hint + native share (mobile) or WhatsApp/Facebook/X/Email + copy link.
 * The link carries `?ref=<sessionId>` — every friend who enters via it gives
 * the referrer an extra ticket at draw time.
 */
export default function ShareBoost({ game, sessionId, shareText, testIdPrefix = "lottery" }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    if (!sessionId) return "";
    const clean = window.location.pathname.replace(/\?.*$/, "");
    return `${window.location.origin}${clean}?ref=${sessionId}`;
  }, [sessionId]);

  const text = shareText || t("play.lottery_share_text", { title: game?.title || "QRISE" });

  async function nativeShare() {
    try {
      if (navigator.share) {
        await navigator.share({ title: game?.title || "QRISE", text, url: shareUrl });
      }
    } catch {
      /* user cancelled */
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("play.lottery_copy_error"));
    }
  }

  const shareTargets = [
    { id: "whatsapp", label: "WhatsApp", color: "#25D366", icon: <MessageCircle className="h-4 w-4" />,
      href: `https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}` },
    { id: "facebook", label: "Facebook", color: "#1877F2", icon: <FacebookIcon className="h-4 w-4" />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { id: "x", label: "X", color: "#0f172a", icon: <XIcon className="h-4 w-4" />,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}` },
    { id: "email", label: "Email", color: "#64748b", icon: <Mail className="h-4 w-4" />,
      href: `mailto:?subject=${encodeURIComponent(game?.title || "QRISE")}&body=${encodeURIComponent(`${text}\n${shareUrl}`)}` },
  ];

  return (
    <div data-testid={`${testIdPrefix}-share-block`}>
      <div className="mt-8 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5 text-left">
        <div className="flex items-start gap-3">
          <Share2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#c58f17" }} />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700 dark:text-amber-300">
              {t("play.lottery_share_title")}
            </div>
            <p className="mt-1 text-sm text-amber-900/80 dark:text-amber-200/80">
              {t("play.lottery_share_body")}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {typeof navigator !== "undefined" && navigator.share ? (
          <Button
            type="button"
            onClick={nativeShare}
            data-testid={`${testIdPrefix}-native-share-btn`}
            className="w-full rounded-xl h-11 bg-foreground text-background hover:bg-foreground/90"
          >
            <Share2 className="h-4 w-4 mr-1.5" />
            {t("play.lottery_share_button")}
          </Button>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {shareTargets.map((s) => (
              <a
                key={s.id}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card hover:bg-muted transition-colors h-20 justify-center"
                data-testid={`${testIdPrefix}-share-${s.id}`}
                onClick={() => toast.success(t("play.lottery_share_opened"))}
              >
                <span
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full"
                  style={{ background: `${s.color}1a`, color: s.color }}
                >
                  {s.icon}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {s.label}
                </span>
              </a>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-muted/60 px-3 py-2">
          <span
            className="font-mono text-[11px] text-muted-foreground truncate flex-1 text-left"
            title={shareUrl}
            data-testid={`${testIdPrefix}-share-url`}
          >
            {shareUrl}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={copyLink}
            className="h-7 px-2 text-xs"
            data-testid={`${testIdPrefix}-copy-link-btn`}
          >
            {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
            {copied ? t("qr.copied") : t("qr.copy_url")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FacebookIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M13.5 21v-8h2.7l.4-3.1h-3.1v-2c0-.9.3-1.5 1.6-1.5h1.7V3.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4.1 1.5-4.1 4.2V9.9H7.5V13h2.8v8h3.2z" />
    </svg>
  );
}

function XIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.8 3h3.1l-6.8 7.8L22 21h-6.3l-4.9-6.5L5 21H1.9l7.2-8.3L2 3h6.5l4.5 6 4.8-6zm-1.1 16h1.7L7.4 4.8H5.6L16.7 19z" />
    </svg>
  );
}
